import React, { ReactNode, useEffect, useRef } from 'react';
import { Modal } from '../Modal';
import {
  Header,
  LeftArrow,
  ModalContent,
  ModalFooter,
  modalStyles,
  RightArrow,
  sliderStyles,
  SliderContent,
  SliderInfo,
  SliderInfoText,
  carouselStyles,
  ClickZoneLeft,
  ClickZoneRight
} from './styled';
import { GallerySlider } from '../GallerySlider';
import useScreenSize from '../../../hooks/useScreenSize';
import { usePageNumberOnScroll } from '../../Ad/ItemPhotosGallery/usePageNumberOnScroll';
import { GalleryCarousel } from '../GalleryCarousel';
import { Photo } from '../../../graphql/graphql';
import { TargetedBannerIds } from '../../../types';

interface FullScreenGalleryProps {
  isOpen: boolean;
  photos: Photo[];
  initialGalleryIndex: number;
  footer: ReactNode;
  onClose: () => void;
  renderHeader: (
    pageNumber: number,
    shouldHideSliderInfo: boolean
  ) => ReactNode;
  description: string;
}

export const FullScreenGallery: React.FC<FullScreenGalleryProps> = ({
  isOpen,
  photos,
  initialGalleryIndex,
  footer,
  onClose,
  renderHeader,
  description
}) => {
  const { isSmallOrLargeDesktop, isLarge } = useScreenSize();
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    slidesToRender,
    pageNumber,
    scrollToNextPhoto,
    scrollToPrevPhoto,
    scrollToPhoto,
    isBannerPosition,
    isBannerActivated,
    shouldRenderBanner,
    setShouldRenderBanner,
    isNextBanner
  } = usePageNumberOnScroll(
    photos,
    sliderRef,
    scrollRef,
    initialGalleryIndex,
    isLarge
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        scrollToNextPhoto();
      }
      if (e.key === 'ArrowLeft') {
        scrollToPrevPhoto();
      }

      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, scrollToNextPhoto, scrollToPrevPhoto]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalId="current-item-gallery"
      modalStyles={modalStyles}
      cypress="current-item-gallery"
      withAnimation={false}
    >
      <ModalContent>
        <Header>{renderHeader(pageNumber, isBannerPosition)}</Header>
        <SliderContent>
          {isSmallOrLargeDesktop && !isBannerPosition && (
            <>
              <ClickZoneLeft onClick={scrollToPrevPhoto} />
              <ClickZoneRight onClick={scrollToNextPhoto} />
            </>
          )}
          <GallerySlider
            isFullScreen
            photos={slidesToRender}
            description={description}
            scrollRef={scrollRef}
            slideRef={sliderRef}
            scrollToPrevPhoto={scrollToPrevPhoto}
            scrollToNextPhoto={scrollToNextPhoto}
            sliderStyles={sliderStyles}
            shouldRenderBlurredBackground={false}
            isBannerActivated={isBannerActivated}
            isNextBanner={isNextBanner}
            setShouldRenderBanner={setShouldRenderBanner}
            shouldRenderBanner={shouldRenderBanner}
            bannerId={TargetedBannerIds.fullscreenGallery}
            isBannerPosition={isBannerPosition}
            sliderInfo={
              <SliderInfo>
                <SliderInfoText tag="body_2">
                  {pageNumber}/{photos.length}
                </SliderInfoText>
              </SliderInfo>
            }
          />
          {isSmallOrLargeDesktop && (
            <>
              <LeftArrow onClick={scrollToPrevPhoto} />
              <RightArrow onClick={scrollToNextPhoto} />
            </>
          )}
        </SliderContent>
        <ModalFooter $isCentered={photos?.length < 16}>
          {isLarge ? (
            <GalleryCarousel
              photos={photos}
              pageNumber={pageNumber}
              shouldOverlayRender={false}
              scrollToPhoto={scrollToPhoto}
              carouselStyles={carouselStyles}
              carouselPhotoWidth={70}
              description={description}
            />
          ) : (
            footer
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
